import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import api from "../lib/api";
import { formatFCFA } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { TrustBadges } from "../components/storefront/TrustBadges";
import { getSource } from "../lib/source";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [nom, setNom] = useState("");
  const [telephone, setTelephone] = useState("");
  const [commune, setCommune] = useState("");
  const [adresse, setAdresse] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [paiementMethode, setPaiementMethode] = useState("");
  const submittedRef = useRef(false);

  const enabledMethods = (settings?.payment_methods || []).filter((m) => m.enabled);

  useEffect(() => {
    if (!paiementMethode && enabledMethods.length) setPaiementMethode(enabledMethods[0].id);
  }, [enabledMethods, paiementMethode]);

  const zones = settings?.delivery_zones || [];
  const frais = 0; // Livraison gratuite sur tous les articles

  useEffect(() => {
    if (items.length === 0 && !submittedRef.current) navigate("/panier");
  }, [items.length, navigate]);

  const submit = async () => {
    if (!nom.trim() || !telephone.trim() || !commune) {
      toast.error("Merci de renseigner votre nom, téléphone et commune.");
      return;
    }
    if (telephone.replace(/\D/g, "").length < 8) {
      toast.error("Numéro de téléphone invalide.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        client: { nom, telephone, commune, adresse },
        items: items.map((i) => ({ product_id: i.product_id, taille: i.taille, quantite: i.quantite })),
        paiement_methode: paiementMethode || "wave",
        source: getSource(),
      });
      sessionStorage.setItem("yoyo_last_phone", telephone);
      submittedRef.current = true;
      navigate(`/commande/confirmation/${data.numero}?cle=${encodeURIComponent(data.cle)}`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur lors de la commande.";
      toast.error(msg);
      if (e.response?.status === 409) {
        // pièce vendue entre-temps : renvoyer au panier pour révision
        setTimeout(() => navigate("/panier"), 1500);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) return null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:py-14">
      <h1 className="mb-8 font-serif text-3xl font-medium text-[#2C1A14] sm:text-4xl">Finaliser la commande</h1>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-3">
          <div>
            <Label htmlFor="nom">Nom complet *</Label>
            <Input id="nom" data-testid="checkout-nom" value={nom} onChange={(e) => setNom(e.target.value)} className="mt-1 bg-white" placeholder="Votre nom" />
          </div>
          <div>
            <Label htmlFor="tel">Téléphone (WhatsApp) *</Label>
            <Input id="tel" data-testid="checkout-telephone" value={telephone} onChange={(e) => setTelephone(e.target.value)} className="mt-1 bg-white" placeholder="+225 07 00 00 00 00" />
          </div>
          <div>
            <Label>Commune de livraison *</Label>
            <Select value={commune} onValueChange={setCommune}>
              <SelectTrigger data-testid="checkout-commune" className="mt-1 bg-white"><SelectValue placeholder="Choisir votre commune" /></SelectTrigger>
              <SelectContent>
                {zones.map((z) => (
                  <SelectItem key={z.nom} value={z.nom}>{z.nom}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="adr">Adresse / point de repère</Label>
            <Textarea id="adr" data-testid="checkout-adresse" value={adresse} onChange={(e) => setAdresse(e.target.value)} className="mt-1 bg-white" placeholder="Quartier, rue, repère..." />
          </div>

          <div>
            <Label>Mode de paiement *</Label>
            <div className="mt-2 space-y-2">
              {enabledMethods.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setPaiementMethode(m.id)}
                  data-testid={`payment-method-${m.id}`}
                  className={`w-full rounded-xl border p-4 text-left transition-all ${paiementMethode === m.id ? "border-[#D76E4D] bg-[#F6E4DE]" : "border-[#E8D5CE] bg-white hover:border-[#D76E4D]"}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${paiementMethode === m.id ? "border-[#D76E4D]" : "border-[#9B7E73]"}`}>
                      {paiementMethode === m.id && <span className="h-2 w-2 rounded-full bg-[#D76E4D]" />}
                    </span>
                    <span className="font-medium text-[#2C1A14]">{m.label}</span>
                  </div>
                  <p className="mt-1 pl-6 text-xs leading-relaxed text-[#6E544A]">{m.instructions}</p>
                </button>
              ))}
              {enabledMethods.length === 0 && <p className="text-sm text-[#9B7E73]">Aucun mode de paiement disponible.</p>}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="sticky top-24 rounded-xl bg-[#F5E8E3] p-6">
            <h3 className="mb-4 font-serif text-xl text-[#2C1A14]">Récapitulatif</h3>
            <div className="space-y-2 text-sm">
              {items.map((it) => (
                <div key={`${it.product_id}-${it.taille}`} className="flex justify-between text-[#6E544A]">
                  <span>{it.nom} ({it.taille}) ×{it.quantite}</span>
                  <span>{formatFCFA(it.prix * it.quantite)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-1 border-t border-[#E8D5CE] pt-4 text-sm">
              <div className="flex justify-between text-[#6E544A]"><span>Sous-total</span><span>{formatFCFA(subtotal)}</span></div>
              <div className="flex justify-between text-[#6E544A]"><span>Livraison Yango</span><span className="font-medium text-[#1da851]">Gratuite</span></div>
              <div className="flex justify-between pt-2 text-lg font-semibold text-[#2C1A14]"><span>Total</span><span data-testid="checkout-total">{formatFCFA(subtotal + frais)}</span></div>
            </div>
            <Button
              onClick={submit}
              disabled={submitting}
              data-testid="checkout-submit-button"
              className="mt-6 w-full rounded-full bg-[#D76E4D] py-6 text-base text-white hover:bg-[#C05A3A]"
            >
              {submitting ? "Traitement..." : "Valider ma commande"}
            </Button>
            <p className="mt-3 text-center text-xs text-[#9B7E73]">Paiement : {enabledMethods.find((m) => m.id === paiementMethode)?.label || "à confirmer"}</p>
            <TrustBadges className="mt-5 border-t border-[#E8D5CE] pt-4" />
          </div>
        </div>
      </div>
    </div>
  );
}
